import { useTranslation } from 'react-i18next';
import { ELEMENT_CATALOG } from './icons';
import type { FloorPlanLayer } from './layers';

interface PaletteItem {
  type: string;
  labelKey: string;
  subLabelKey: string;
}

interface PaletteGroup {
  labelKey: string;
  layer: FloorPlanLayer;
  items: PaletteItem[];
}

const PALETTE_GROUPS: PaletteGroup[] = [
  {
    labelKey: 'floor_plan.palette_group_lighting',
    layer: 'lighting',
    items: [
      { type: 'lighting_single', labelKey: 'floor_plan.item_lighting_single', subLabelKey: 'floor_plan.item_lighting_single_sub' },
      { type: 'exhaust_fan', labelKey: 'floor_plan.item_exhaust_fan', subLabelKey: 'floor_plan.item_exhaust_fan_sub' },
      { type: 'lighting_spot', labelKey: 'floor_plan.item_lighting_spot', subLabelKey: 'floor_plan.item_lighting_spot_sub' },
      { type: 'switch', labelKey: 'floor_plan.item_switch', subLabelKey: 'floor_plan.item_switch_sub' },
      { type: 'switch_double', labelKey: 'floor_plan.item_switch_double', subLabelKey: 'floor_plan.item_switch_double_sub' },
      { type: 'master_switch', labelKey: 'floor_plan.item_master_switch', subLabelKey: 'floor_plan.item_master_switch_sub' },
    ],
  },
  {
    labelKey: 'floor_plan.palette_group_blocks',
    layer: 'blocks',
    items: [
      { type: 'custom_block', labelKey: 'floor_plan.item_custom_block', subLabelKey: 'floor_plan.item_custom_block_sub' },
    ],
  },
  {
    labelKey: 'floor_plan.palette_group_sockets',
    layer: 'sockets',
    items: [
      { type: 'socket_single', labelKey: 'floor_plan.item_socket_single', subLabelKey: 'floor_plan.item_socket_single_sub' },
      { type: 'socket_double', labelKey: 'floor_plan.item_socket_double', subLabelKey: 'floor_plan.item_socket_double_sub' },
      { type: 'socket_triple', labelKey: 'floor_plan.item_socket_triple', subLabelKey: 'floor_plan.item_socket_triple_sub' },
      { type: 'socket_quadruple', labelKey: 'floor_plan.item_socket_quadruple', subLabelKey: 'floor_plan.item_socket_quadruple_sub' },
      { type: 'bathroom_socket', labelKey: 'floor_plan.item_bathroom_socket', subLabelKey: 'floor_plan.item_bathroom_socket_sub' },
      { type: 'kitchen_socket', labelKey: 'floor_plan.item_kitchen_socket', subLabelKey: 'floor_plan.item_kitchen_socket_sub' },
      { type: 'tv_socket', labelKey: 'floor_plan.item_tv_socket', subLabelKey: 'floor_plan.item_tv_socket_sub' },
      { type: 'rj45_socket', labelKey: 'floor_plan.item_rj45_socket', subLabelKey: 'floor_plan.item_rj45_socket_sub' },
    ],
  },
  {
    labelKey: 'floor_plan.palette_group_appliances',
    layer: 'appliances',
    items: [
      { type: 'washing_machine', labelKey: 'floor_plan.item_washing_machine', subLabelKey: 'floor_plan.item_washing_machine_sub' },
      { type: 'oven', labelKey: 'floor_plan.item_oven', subLabelKey: 'floor_plan.item_oven_sub' },
      { type: 'hob', labelKey: 'floor_plan.item_hob', subLabelKey: 'floor_plan.item_hob_sub' },
      { type: 'microwave', labelKey: 'floor_plan.item_microwave', subLabelKey: 'floor_plan.item_microwave_sub' },
      { type: 'extractor_hood', labelKey: 'floor_plan.item_extractor_hood', subLabelKey: 'floor_plan.item_extractor_hood_sub' },
      { type: 'refrigerator', labelKey: 'floor_plan.item_refrigerator', subLabelKey: 'floor_plan.item_refrigerator_sub' },
      { type: 'freezer', labelKey: 'floor_plan.item_freezer', subLabelKey: 'floor_plan.item_freezer_sub' },
      { type: 'air_conditioner', labelKey: 'floor_plan.item_air_conditioner', subLabelKey: 'floor_plan.item_air_conditioner_sub' },
      { type: 'boiler', labelKey: 'floor_plan.item_boiler', subLabelKey: 'floor_plan.item_boiler_sub' },
      { type: 'dryer', labelKey: 'floor_plan.item_dryer', subLabelKey: 'floor_plan.item_dryer_sub' },
      { type: 'electric_heater', labelKey: 'floor_plan.item_electric_heater', subLabelKey: 'floor_plan.item_electric_heater_sub' },
      { type: 'ev_charger', labelKey: 'floor_plan.item_ev_charger', subLabelKey: 'floor_plan.item_ev_charger_sub' },
      { type: 'hydromassage_bath', labelKey: 'floor_plan.item_hydromassage_bath', subLabelKey: 'floor_plan.item_hydromassage_bath_sub' },
      { type: 'dishwasher', labelKey: 'floor_plan.item_dishwasher', subLabelKey: 'floor_plan.item_dishwasher_sub' },
      { type: 'automation', labelKey: 'floor_plan.item_automation', subLabelKey: 'floor_plan.item_automation_sub' },
    ],
  },
];

interface ElementPaletteProps {
  selected: string | null;
  onSelect: (type: string | null) => void;
  onStartCustomBlock?: () => void;
  hiddenLayers: Set<FloorPlanLayer>;
  onToggleLayer: (layer: FloorPlanLayer) => void;
  onShowLayer: (layer: FloorPlanLayer) => void;
  iconSize: number;
  onIconSizeChange: (size: number) => void;
  compactIcons: boolean;
  onCompactIconsChange: (enabled: boolean) => void;
}

export function ElementPalette({
  selected,
  onSelect,
  onStartCustomBlock,
  hiddenLayers,
  onToggleLayer,
  onShowLayer,
  iconSize,
  onIconSizeChange,
  compactIcons,
  onCompactIconsChange,
}: ElementPaletteProps) {
  const { t } = useTranslation();

  const getPreviewWidthClass = (type: string) => {
    if (type === 'socket_quadruple') return 'min-w-14';
    if (type === 'socket_triple') return 'min-w-12';
    if (type === 'socket_double') return 'min-w-10';
    return 'min-w-8';
  };

  const EyeIcon = ({ hidden }: { hidden: boolean }) => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {hidden ? (
        <>
          <path d="M3 3l18 18" />
          <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" />
          <path d="M9.9 5.1A9.7 9.7 0 0 1 12 5c5 0 8.5 4.5 9.7 6.4a1.2 1.2 0 0 1 0 1.2 18 18 0 0 1-2.8 3.4" />
          <path d="M6.6 6.6A18 18 0 0 0 2.3 11.4a1.2 1.2 0 0 0 0 1.2C3.5 14.5 7 19 12 19a9.6 9.6 0 0 0 4-.9" />
        </>
      ) : (
        <>
          <path d="M2.3 11.4C3.5 9.5 7 5 12 5s8.5 4.5 9.7 6.4a1.2 1.2 0 0 1 0 1.2C20.5 14.5 17 19 12 19s-8.5-4.5-9.7-6.4a1.2 1.2 0 0 1 0-1.2Z" />
          <circle cx="12" cy="12" r="3" />
        </>
      )}
    </svg>
  );

  return (
    <details open className="shrink-0 bg-surface-2">
      <summary
        onKeyDown={event => {
          // Keep Space for native disclosure, not the canvas pan shortcut.
          if (event.code === 'Space') event.stopPropagation();
        }}
        className="min-h-11 cursor-pointer border-b border-border px-3 py-3 font-sans text-sm font-semibold text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
      >
        {t('floor_plan.elements')}
      </summary>
      {/* Header */}
      <div className="px-3 py-3 border-b border-border">
        <div className="text-[11px] text-ink-4 mt-1 leading-relaxed">
          {t('floor_plan.hint_controls')}
        </div>
      </div>

      <div className="border-b border-border px-3 py-3">
        <div>
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span className="text-[11px] font-medium text-ink-4">{t('floor_plan.icons')}</span>
            <span className="min-w-[34px] text-right font-mono text-[11px] text-ink-3">{iconSize}px</span>
          </div>
          <input
            type="range"
            min={12}
            max={60}
            step={1}
            value={iconSize}
            onChange={event => onIconSizeChange(Number(event.target.value))}
            className="w-full accent-accent"
          />
          <label className="mt-2 flex min-h-11 cursor-pointer items-center gap-2 text-xs text-ink">
            <input
              type="checkbox"
              checked={compactIcons}
              onChange={event => onCompactIconsChange(event.target.checked)}
              className="accent-accent"
            />
            {t('floor_plan.compact_icons')}
          </label>
          <p className="text-[11px] leading-relaxed text-ink-4">{t('floor_plan.compact_icons_hint')}</p>
        </div>
      </div>

      {/* Groups */}
      {PALETTE_GROUPS.map(group => {
        const isLayerHidden = hiddenLayers.has(group.layer);
        const groupLabel = t(group.labelKey);
        const visibilityLabel = t(isLayerHidden ? 'floor_plan.show_layer' : 'floor_plan.hide_layer', { layer: groupLabel });

        return (
        <div key={group.labelKey}>
          <div className="flex items-center justify-between gap-2 px-3 pt-2.5 pb-1">
            <span className={[
              'min-w-0 text-[11px] font-medium',
              isLayerHidden ? 'text-ink-3' : 'text-ink-4',
            ].join(' ')}>
              {groupLabel}
            </span>
            <button
              type="button"
              onClick={() => onToggleLayer(group.layer)}
              aria-label={visibilityLabel}
              title={visibilityLabel}
              aria-pressed={!isLayerHidden}
              className={[
                'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded border transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 focus-visible:ring-offset-1 focus-visible:ring-offset-surface-2',
                isLayerHidden
                  ? 'border-border text-ink-4 hover:bg-surface-3 hover:text-ink'
                  : 'border-accent/35 bg-accent/10 text-accent hover:bg-accent/15',
              ].join(' ')}
            >
              <EyeIcon hidden={isLayerHidden} />
            </button>
          </div>
          <div className="flex flex-col gap-0.5 px-2 pb-2">
            {group.items.map(item => {
              const isSelected = selected === item.type;
              const catalogItem = ELEMENT_CATALOG[item.type];
              const Icon = catalogItem?.Icon;
              const label = t(item.labelKey);
              const subLabel = t(item.subLabelKey);

              return (
                <button
                  key={item.type}
                  onClick={() => {
                    if (isLayerHidden) onShowLayer(group.layer);
                    if (item.type === 'custom_block') {
                      onStartCustomBlock?.();
                      return;
                    }
                    onSelect(isSelected ? null : item.type);
                  }}
                  title={label}
                  aria-label={label}
                  className={[
                    'flex w-full items-center gap-2 rounded-md border px-2 py-1.5 text-left',
                    'transition-all duration-150 active:scale-[0.99]',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 focus-visible:ring-offset-1 focus-visible:ring-offset-surface-2',
                    isLayerHidden ? 'opacity-65' : '',
                    isSelected
                      ? 'border-accent/50 bg-accent/10 shadow-sm'
                      : 'border-border/60 bg-surface/50 hover:border-border-2 hover:bg-surface hover:shadow-sm',
                  ].join(' ')}
                >
                  <div
                    className={[
                      'flex h-8 items-center justify-center rounded-md border transition-colors',
                      'flex-shrink-0 bg-surface-2',
                      getPreviewWidthClass(item.type),
                      isSelected ? 'border-accent/35 bg-accent/10' : 'border-border/60',
                    ].join(' ')}
                  >
                    {Icon ? (
                      <Icon size={20} color="currentColor" />
                    ) : (
                      <span className="h-2.5 w-2.5 rounded-full border border-current" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className={`text-xs font-medium leading-tight break-words ${isSelected ? 'text-accent' : 'text-ink'}`}>
                      {label}
                    </div>
                    <div className="font-mono text-[11px] text-ink-4 mt-0.5">{subLabel}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        );
      })}
    </details>
  );
}
